/**
 * Hypotheses card component displaying testable performance hypotheses
 * Shows 3-5 hypotheses with statement, change, metric, impact, mechanism, and risk
 */

import { Card } from '@/components/ui/Card';
import type { PerformanceHypothesis } from '@/types';

import { HypothesisItem } from './HypothesisItem';

export interface HypothesesCardProps {
  hypotheses: PerformanceHypothesis[];
  className?: string;
}

export function HypothesesCard({ hypotheses, className }: HypothesesCardProps) {
  return (
    <Card className={className}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <svg
            className="w-5 h-5 text-gray-700"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />
          </svg>
          <h3 className="text-lg font-bold text-gray-900">Performance Hypotheses</h3>
        </div>
        <p className="text-sm text-gray-500">
          Testable predictions for creative optimization. Frame as hypotheses to validate, not predictions.
        </p>
      </div>

      {/* Hypotheses List */}
      <div className="space-y-4">
        {hypotheses.map((hypothesis, index) => (
          <HypothesisItem
            key={`hypothesis-${index}`}
            hypothesis={hypothesis}
            index={index}
          />
        ))}
      </div>

      {/* Empty State */}
      {hypotheses.length === 0 && (
        <div className="py-12 text-center">
          <svg
            className="w-12 h-12 mx-auto text-gray-300 mb-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />
          </svg>
          <p className="text-sm text-gray-500">No hypotheses generated</p>
          <p className="text-xs text-gray-400 mt-1">
            Re-run the analysis to generate performance hypotheses
          </p>
        </div>
      )}
    </Card>
  );
}
